import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { COUNTRIES, Country } from "@/lib/validation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  defaultCountry?: string;
  error?: string;
  id?: string;
  autoComplete?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findCountry(code: string): Country {
  return COUNTRIES.find((c) => c.code === code) ?? COUNTRIES[0];
}

/**
 * Strips any existing dial-code prefix from the raw input string and returns
 * the local part only (may be empty string).
 */
function stripDialCode(value: string, dialCode: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith(dialCode)) {
    return trimmed.slice(dialCode.length).trimStart();
  }
  return trimmed;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PhoneInput({
  value,
  onChange,
  onBlur,
  defaultCountry = "DE",
  error,
  id,
  autoComplete,
  className,
}: PhoneInputProps) {
  const { t } = useTranslation("search");
  const [selectedCountry, setSelectedCountry] = React.useState<Country>(() =>
    findCountry(defaultCountry),
  );
  const [open, setOpen] = React.useState(false);

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when user clicks outside the component
  React.useEffect(() => {
    if (!open) return;

    function handleOutsideClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  // Derive the local part from the controlled value so the text input stays
  // in sync when the parent resets or pre-fills the field.
  const localPart = stripDialCode(value, selectedCountry.dialCode);

  function handleCountrySelect(country: Country) {
    setSelectedCountry(country);
    setOpen(false);
    // Reconstruct full value with new dial code, preserving the local part
    const currentLocal = stripDialCode(value, selectedCountry.dialCode);
    onChange(currentLocal ? `${country.dialCode} ${currentLocal}` : "");
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    // Always prefix with the active dial code so the parent receives a
    // fully-qualified phone string.
    onChange(raw ? `${selectedCountry.dialCode} ${raw}` : "");
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {/* Input row */}
      <div
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background",
          "text-base ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          error && "border-destructive focus-within:ring-destructive",
        )}
      >
        {/* Country selector button */}
        <button
          type="button"
          aria-label={t("phoneInput.selectCountry")}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className={cn(
            "flex shrink-0 items-center gap-1.5 rounded-l-md px-3",
            "border-r border-input bg-transparent",
            "text-sm text-foreground",
            "hover:bg-muted hover:text-foreground",
            "focus:outline-none",
            "min-w-[4.5rem]",
          )}
        >
          <span aria-hidden="true">{selectedCountry.flag}</span>
          <span className="tabular-nums">{selectedCountry.dialCode}</span>
          {/* Chevron */}
          <svg
            aria-hidden="true"
            className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {/* Phone number text input */}
        <input
          id={id}
          type="text"
          inputMode="tel"
          autoComplete={autoComplete}
          value={localPart}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={t("phoneInput.placeholder")}
          className={cn(
            "flex-1 rounded-r-md bg-transparent px-3 py-2",
            "text-base placeholder:text-muted-foreground md:text-sm",
            "focus:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
      </div>

      {/* Inline error message */}
      {error && (
        <p className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* Country dropdown */}
      {open && (
        <div
          role="listbox"
          aria-label={t("phoneInput.selectCountry")}
          className={cn(
            "absolute left-0 top-full z-50 mt-1",
            "w-full min-w-[14rem]",
            "rounded-md border border-border bg-popover shadow-md",
            "max-h-60 overflow-y-auto",
          )}
        >
          {COUNTRIES.map((country) => {
            const isSelected = country.code === selectedCountry.code;
            return (
              <button
                key={country.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleCountrySelect(country)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm",
                  "text-left text-popover-foreground",
                  "hover:bg-muted hover:text-foreground",
                  "focus:outline-none focus:bg-muted",
                  isSelected && "bg-primary/10 font-medium",
                )}
              >
                <span aria-hidden="true" className="text-base">
                  {country.flag}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {country.dialCode}
                </span>
                <span className="truncate">{country.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
